/**
 * Utilitários de Formatação
 * 
 * Funções reutilizáveis para formatação de dados
 */

/**
 * Formata um valor numérico como preço
 * 
 * @param {number|string} price - Valor a ser formatado
 * @param {Object} options - Opções de formatação
 * @param {boolean} options.showCurrency - Se deve exibir símbolo da moeda (padrão: true)
 * @param {number} options.minimumFractionDigits - Dígitos decimais mínimos (padrão: 2)
 * @param {number} options.maximumFractionDigits - Dígitos decimais máximos (padrão: 2)
 * @param {string} options.currency - Código da moeda (padrão: 'BRL')
 * @param {string} options.locale - Locale para formatação (padrão: 'pt-BR')
 * 
 * @returns {string} Preço formatado (ex: "R$ 99,90")
 */
export const formatPrice = (price, options = {}) => {
  const {
    showCurrency = true,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    currency = 'BRL',
    locale = 'pt-BR',
  } = options;

  const numPrice = typeof price === 'string' ? parseFloat(price) : price;

  if (isNaN(numPrice)) {
    // Fallback simples
    return showCurrency ? 'R$ 0,00' : '0,00';
  }

  try {
    const formatted = new Intl.NumberFormat(locale, {
      style: showCurrency ? 'currency' : 'decimal',
      currency: currency,
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(numPrice);

    return formatted;
  } catch (error) {
    // Fallback se Intl.NumberFormat falhar
    console.warn('Erro ao formatar preço:', error);
    return showCurrency ? `${currency} ${numPrice.toFixed(maximumFractionDigits)}` : numPrice.toFixed(maximumFractionDigits);
  }
};

/**
 * Formata um valor numérico como preço sem símbolo de moeda
 * 
 * @param {number|string} price - Valor a ser formatado
 * @returns {string} Preço formatado (ex: "99,90")
 */
export const formatPriceNumber = (price) => {
  return formatPrice(price, { showCurrency: false });
};

/**
 * Calcula o desconto de 10% para pagamento via Pix
 * 
 * @param {number|string} price - Preço original
 * @returns {number} Preço com desconto aplicado
 */
export const calculatePixDiscount = (price) => {
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  if (isNaN(numPrice)) return 0;
  return numPrice * 0.9;
};

/**
 * Formata o preço com desconto Pix
 * 
 * @param {number|string} price - Preço original
 * @returns {string} Preço formatado com desconto (ex: "R$ 89,10")
 */
export const formatPixPrice = (price) => {
  const discountedPrice = calculatePixDiscount(price);
  return formatPrice(discountedPrice);
};

/**
 * Calcula o valor da parcela (3x sem juros)
 * 
 * @param {number|string} price - Preço total
 * @param {number} installments - Número de parcelas (padrão: 3)
 * @returns {number} Valor da parcela
 */
export const calculateInstallment = (price, installments = 3) => {
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  if (isNaN(numPrice) || installments <= 0) return 0;
  return numPrice / installments;
};

/**
 * Formata o valor da parcela
 * 
 * @param {number|string} price - Preço total
 * @param {number} installments - Número de parcelas (padrão: 3)
 * @returns {string} Valor da parcela formatado (ex: "R$ 33,00")
 */
export const formatInstallment = (price, installments = 3) => {
  const installmentValue = calculateInstallment(price, installments);
  return formatPrice(installmentValue);
};

/**
 * Formata CEP (adiciona hífen se necessário)
 * 
 * @param {string} cep - CEP sem formatação
 * @returns {string} CEP formatado (ex: "12345-678")
 */
export const formatCEP = (cep) => {
  if (!cep) return '';
  
  // Remove tudo que não é número
  const numbers = cep.replace(/\D/g, '');
  
  // Aplica máscara
  if (numbers.length <= 5) {
    return numbers;
  }
  
  return `${numbers.slice(0, 5)}-${numbers.slice(5, 8)}`;
};

/**
 * Formata telefone brasileiro
 * 
 * @param {string} phone - Telefone sem formatação
 * @returns {string} Telefone formatado
 */
export const formatPhone = (phone) => {
  if (!phone) return '';
  
  // Remove tudo que não é número
  const numbers = phone.replace(/\D/g, '');
  
  // Aplica máscara baseada no tamanho
  if (numbers.length <= 10) {
    // Telefone fixo: (XX) XXXX-XXXX
    return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  } else {
    // Celular: (XX) 9XXXX-XXXX
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
};

/**
 * Formata CPF
 * 
 * @param {string} cpf - CPF sem formatação
 * @returns {string} CPF formatado (ex: "123.456.789-00")
 */
export const formatCPF = (cpf) => {
  if (!cpf) return '';
  
  // Remove tudo que não é número
  const numbers = cpf.replace(/\D/g, '');
  
  // Aplica máscara
  if (numbers.length <= 3) {
    return numbers;
  } else if (numbers.length <= 6) {
    return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
  } else if (numbers.length <= 9) {
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
  } else {
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
  }
};

/**
 * Formata data para exibição
 * 
 * @param {Date|string} date - Data a ser formatada
 * @param {Object} options - Opções de formatação
 * @param {boolean} options.includeTime - Incluir hora (padrão: false)
 * @param {string} options.locale - Locale para formatação (padrão: 'pt-BR')
 * @returns {string} Data formatada
 */
export const formatDate = (date, options = {}) => {
  const {
    includeTime = false,
    locale = 'pt-BR',
  } = options;

  if (!date) return '';

  const dateObj = date instanceof Date ? date : new Date(date);
  
  if (isNaN(dateObj.getTime())) return '';

  const dateOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  if (includeTime) {
    dateOptions.hour = '2-digit';
    dateOptions.minute = '2-digit';
  }

  try {
    return new Intl.DateTimeFormat(locale, dateOptions).format(dateObj);
  } catch (error) {
    console.warn('Erro ao formatar data:', error);
    // Fallback simples
    return dateObj.toLocaleDateString();
  }
};

/**
 * Formata quantidade de itens
 * 
 * @param {number} quantity - Quantidade
 * @param {string} singular - Texto no singular (padrão: "item")
 * @param {string} plural - Texto no plural (padrão: "itens")
 * @returns {string} Quantidade formatada (ex: "1 item" ou "5 itens")
 */
export const formatQuantity = (quantity, singular = 'item', plural = 'itens') => {
  const numQuantity = typeof quantity === 'string' ? parseInt(quantity, 10) : quantity;
  
  if (isNaN(numQuantity) || numQuantity < 0) return `0 ${plural}`;
  
  return `${numQuantity} ${numQuantity === 1 ? singular : plural}`;
};

/**
 * Trunca texto com ellipsis
 * 
 * @param {string} text - Texto a ser truncado
 * @param {number} maxLength - Tamanho máximo
 * @returns {string} Texto truncado
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
};

